'use client';

import { useEffect, useState } from 'react';

/**
 * Palette-wash hero background: three tones sampled from the dancer's photo
 * (top / middle / bottom zones), mixed ~72% into the theme ground and laid as
 * soft radial fields. Runs client-side against the already-displayed image —
 * works for every row (incl. legacy media with no stored palette), costs one
 * 32px canvas pass, and is cached per media id for the session.
 *
 * Near-white pixels are skipped so studio-backdrop headshots key off the
 * SUBJECT (skin, costume), not the seamless paper. On any failure (CORS-
 * tainted canvas, decode error) the tones stay null and only the base stage
 * radial (pure theme tokens, rendered by the server parent) shows — the wash
 * is strictly progressive enhancement.
 */
export function HeroWash({
  src,
  cacheKey,
}: {
  src: string | null;
  cacheKey: string | null;
}) {
  const [tones, setTones] = useState<[string, string, string] | null>(null);

  useEffect(() => {
    if (!src) return;
    const storageKey = `hero-tones:${cacheKey ?? src}`;

    try {
      const cached = window.sessionStorage.getItem(storageKey);
      if (cached) {
        // Deferred (not sync-in-effect): keeps the hydration pass identical
        // to the server render and satisfies react-hooks/set-state-in-effect.
        const id = window.setTimeout(() => {
          setTones(JSON.parse(cached) as [string, string, string]);
        }, 0);
        return () => window.clearTimeout(id);
      }
    } catch {
      // storage unavailable (private mode) — extract without caching
    }

    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (cancelled) return;
      try {
        const size = 32;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(img, 0, 0, size, size);
        const data = ctx.getImageData(0, 0, size, size).data;

        const zones: Array<[number, number]> = [
          [0, 13], // top ~40%
          [13, 26], // middle ~40%
          [26, 32], // bottom ~20%
        ];
        const sampled = zones.map(([y0, y1]) => {
          let r = 0;
          let g = 0;
          let b = 0;
          let n = 0;
          for (let y = y0; y < y1; y++) {
            for (let x = 0; x < size; x++) {
              const i = (y * size + x) * 4;
              const lum = (data[i]! + data[i + 1]! + data[i + 2]!) / 3;
              if (lum > 235) continue; // skip studio-white backdrop
              r += data[i]!;
              g += data[i + 1]!;
              b += data[i + 2]!;
              n++;
            }
          }
          if (n === 0) return 'rgb(138 138 138)';
          // Chroma boost 1.6× around the zone's own average so muted photo
          // tones survive the mix toward the theme ground.
          const R = r / n;
          const G = g / n;
          const B = b / n;
          const avg = (R + G + B) / 3;
          const boost = (v: number) =>
            Math.round(Math.max(0, Math.min(255, avg + (v - avg) * 1.6)));
          return `rgb(${boost(R)} ${boost(G)} ${boost(B)})`;
        }) as [string, string, string];

        setTones(sampled);
        try {
          window.sessionStorage.setItem(storageKey, JSON.stringify(sampled));
        } catch {
          // best-effort cache only
        }
      } catch {
        // tainted canvas / decode error → no wash, base stage radial remains
      }
    };
    img.src = src;
    return () => {
      cancelled = true;
    };
  }, [src, cacheKey]);

  if (!tones) return null;

  // Tones mix toward the theme ground. `--background` is a FULL color in
  // this design system (e.g. `hsl(36 30% 98.5%)`), so it is used directly —
  // wrapping it in hsl() would produce an invalid color and void the whole
  // gradient (the bug that shipped the first deploy of this component).
  const mix = (tone: string, amount: string) =>
    `color-mix(in oklab, ${tone}, var(--background) ${amount})`;

  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 transition-opacity duration-700"
      style={{
        backgroundImage: [
          `radial-gradient(ellipse 75% 65% at 26% 20%, ${mix(tones[0], '53%')} 0%, transparent 74%)`,
          `radial-gradient(ellipse 70% 60% at 76% 36%, ${mix(tones[1], '53%')} 0%, transparent 76%)`,
          `radial-gradient(ellipse 95% 70% at 50% 88%, ${mix(tones[2], '65%')} 0%, transparent 78%)`,
        ].join(', '),
      }}
    />
  );
}
